import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/input/TextArea";
import BigButton from "components/atoms/buttons/BigButton";
import MapUpload, {
  MapUploadImage,
} from "components/atoms/input/upload/map/MapUpload";
import AddressInput from "components/atoms/input/address/AddressInput";
import AddressMap from "components/atoms/input/address/AddressMap";
import type { Option } from "utils/options";
import { RecorderEditInfo } from "api/interfaces/recorderInterface.interface";
import { UPDATE_TYPE } from "./EditListContainer";
import SelectComponent from "components/atoms/select/SelectComponent";
import {
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { convertType } from "utils/functions";
import { deRegistrationSupportCheck } from "utils/RecorderUtil";
import Deactivate from "components/blocks/modal/deactivate";
import { useMemo } from "react";
import Spinner from "components/atoms/loader/Spinner";

interface Props {
  type: string;
  mergedCount: number;
  isSystem: boolean;
  onModal: (type?: string) => void;
  isLoaded: boolean;
  selected: google.maps.LatLngLiteral;
  onChangeLocationSelect: (
    address: google.maps.LatLngLiteral,
    addressString: string
  ) => void;
  onChangeRecordInfo: (
    type: UPDATE_TYPE,
    value: number | string | boolean | string[]
  ) => void;
  onChangeAddress: (e: any) => void;
  data: RecorderEditInfo;
  onSave: () => void;
  imgFile: any;
  saveImgFile: (e: any) => void;
  onDeleteImgFile: () => void;
  imgRef: any;
  groupData: Option[];
  groupIsFetching: boolean;
  selectGroup: string[];
  mapRef: any;
  onMarkerChange: (
    address: google.maps.LatLngLiteral,
    addressString: string
  ) => void;
  onClickDelete: () => void;
  validate: Validate;
  validateMsg: ValidateMsg;
  selectedAccount: ProfileAccountInfo;
  registeredDate: string;
  brand?: string;
  disabled: boolean;
  deleteDisabled: boolean;
  mergedSystemName?: string;
  isLoading: boolean;
  isDetached?: boolean;
  onClickDisable: () => void;
}

export function EditListPresenter(props: Props): JSX.Element {
  const deactiveDisabled = useMemo(() => {
    const typeString = convertType(props.data);
    if (props.isSystem) {
      return false;
    }
    return !deRegistrationSupportCheck(typeString, props.data.firmwareVersion);
  }, [props.data, props.isSystem]);
  const isMergedSystem = useMemo(
    () => props.isSystem || props.mergedCount > 1,
    [props.isSystem, props.mergedCount]
  );
  return (
    <BigPopupTemplate
      title={props.isSystem ? "Edit System" : "Edit Recorder"}
      onModal={props.onModal}
    >
      {props.isLoading ? (
        <Spinner />
      ) : (
        <s.CloudContainer>
          <ModalInput
            label={props.isSystem ? "System Name" : "Recorder Name"}
            essential
          >
            <Input
              placeholder="Please enter a name"
              value={props.data.name}
              disabled={true}
              onChange={(e) =>
                props.onChangeRecordInfo(UPDATE_TYPE.NAME, e.target.value)
              }
              data-testid="recorders-edit-modal-input-change-record-info"
            />
          </ModalInput>

          <ModalInput label="Location" essential>
            {/* <Input placeholder="Enter this recorders address." /> */}
            <AddressInput
              isLoaded={props.isLoaded}
              defaultValue={props.data.location.location}
              value={props.data.location.location}
              onChange={props.onChangeAddress}
              onChangeLocationSelect={props.onChangeLocationSelect}
              data-testid="recorders-edit-modal-address-input-change-address"
            />

            {props.validate.address && (
              <ValidateWord data-testid="recorders-edit-modal-validate-address">
                {props.validateMsg.address}
              </ValidateWord>
            )}

            <s.Map>
              <AddressMap
                isLoaded={props.isLoaded}
                selected={props.selected}
                ref={props.mapRef}
                onMarkerChange={props.onMarkerChange}
              />
            </s.Map>
          </ModalInput>

          <s.EditTwopart>
            <ModalInput label="Recorder Group">
              <SelectComponent
                mode="multiple"
                options={props.groupData}
                loading={props.groupIsFetching}
                value={props.selectGroup}
                onChange={(value) =>
                  props.onChangeRecordInfo(
                    UPDATE_TYPE.RECORDER_GROUP,
                    (value as string[]) || []
                  )
                }
              />
            </ModalInput>

            <ModalInput label="Registered Date">
              <Input
                //placeholder="Enter installation date"
                //value={props.data.installedDate}
                value={props.registeredDate}
                // onChange={(date: Date) =>
                //   props.onChangeRecordInfo(
                //     UPDATE_TYPE.INSTALL_DATE,
                //     date.toISOString()
                //   )
                // }
                disabled
              />
            </ModalInput>
          </s.EditTwopart>

          <ModalInput label="Custom Thumbnail">
            <MapUpload
              onUpload={props.saveImgFile}
              onDelete={props.onDeleteImgFile}
              ref={props.imgRef}
              value={props.imgFile}
            />

            {props.imgFile !== "" && (
              <MapUploadImage>
                <img src={props.imgFile} alt="Recorder_Thumbnail" />
              </MapUploadImage>
            )}
          </ModalInput>
          <ModalInput label="Description">
            <TextArea
              placeholder="Please enter a description"
              defaultValue={props.data.description}
              onChange={(e) =>
                props.onChangeRecordInfo(UPDATE_TYPE.DESC, e.target.value)
              }
              data-testid="recorders-edit-modal-textarea-change-record-info"
            />
          </ModalInput>
          <s.ButtonWrapper>
            <RBACWrapper requiredPermissions={"manageRecordersAndGroups"}>
              {deactiveDisabled ? (
                <BigButton
                  label="Delete"
                  onClickBtn={props.onClickDisable}
                  disabled={props.deleteDisabled || props.disabled}
                  buttonType="warning"
                />
              ) : props.isSystem ? (
                <Deactivate
                  title={
                    <>
                      Deleting the system will deregister it from myDW,
                      <br /> including all merged servers.
                      <br />
                      Are you sure you want to delete this system?
                    </>
                  }
                  subTitle={
                    isMergedSystem
                      ? props.data.mergedSystemName ?? props.data.name
                      : props.data.name
                  }
                  onDeactive={props.onClickDelete}
                  disabled={props.deleteDisabled || props.disabled}
                />
              ) : (
                (!isMergedSystem || props.isDetached) && (
                  <Deactivate
                    title="Are you sure you want to delete this recorder?"
                    subTitle={props.data.name}
                    onDeactive={props.onClickDelete}
                    disabled={props.deleteDisabled || props.disabled}
                  />
                )
              )}
            </RBACWrapper>

            <BigButton
              label="Save"
              disabled={props.disabled}
              onClickBtn={props.onSave}
              data-testid="recorders-edit-modal-button-save"
            />
          </s.ButtonWrapper>
        </s.CloudContainer>
      )}
    </BigPopupTemplate>
  );
}
